/* eslint-disable jira/graphql/restrict-graphql-in-unnamed-files */
import React, { memo } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { DocNode } from '@atlaskit/adf-schema';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { useLazyMediaViewContext } from '../../../../../services/lazy-media-view-context-loader/index.tsx';
import { AsyncRichTextRenderer } from '../rich-text-renderer/index.tsx';
import type { richTextFullIssue$key } from './__generated__/richTextFullIssue.graphql';

export type Props = {
	richText: DocNode;
	issueKey: string;
	issueRef?: richTextFullIssue$key | null;
};

export const RichTextCellFull = memo<Props>(({ richText, issueKey, issueRef }: Props) => {
	const issueData = useFragment<richTextFullIssue$key>(
		graphql`
			fragment richTextFullIssue on JiraIssue
			@argumentDefinitions(
				blockAttachmentDownloadSegregation: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/block-attachment-download-segregation.relayprovider"
				}
			) {
				canBeExported @skip(if: $blockAttachmentDownloadSegregation)
				canDownloadAttachment @include(if: $blockAttachmentDownloadSegregation)
			}
		`,
		issueRef ?? null,
	);

	const viewContext = useLazyMediaViewContext(issueKey);
	let disableMediaDownload = false;
	if (fg('block_attachment_download_segregation')) {
		disableMediaDownload = issueData?.canDownloadAttachment === false;
	} else {
		disableMediaDownload =
			issueData?.canBeExported === false && fg('block_attachment_download_on_data_export_dsp_rule');
	}
	return (
		<Box xcss={richTextCellWrapperStyles}>
			<AsyncRichTextRenderer
				mediaContext={{ viewContext, uploadContext: null, userAuth: null }} // Specify the media context as needed
				adfDocument={richText}
				disableMediaDownload={disableMediaDownload}
			/>
		</Box>
	);
});

const richTextCellWrapperStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
});
